import React from 'react'
import { Button, Typography } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { Activity, Client, ActivityRegistrationStatus } from '../../../pure-js/types/GrooverTypes'
import styled from '@emotion/styled'
import Box from './Box'
import RegistrationStatusButton from './RegistrationStatusButton'
import DropinOnlyStatusButton from './DropinOnlyStatusButton'
import RegistrationQueueNotice from './RegistrationQueueNotice'
import GrooverText from './GrooverText'
import { useActivityPublicCapacity } from '../hooks/useActivityPublicCapacity'
import useAppState from '../hooks/useAppState'
import RoutePath from '../../../pure-js/libs/RoutePath'
import { buildRoute } from '../../../pure-js/libs/RouteBuilder'
import { resolveActivityEndTime } from '../../../pure-js/libs/ActivityTimeHelper'

interface ActivityInfoProps {
  activity: Activity
  client?: Client
}

const RegistrationInfo: React.FC<ActivityInfoProps> = ({ activity, client }) => {
  const { state } = useAppState()
  const language = state.user?.language
  const langQuery = language ? { lang: language } : undefined
  const { isAtOrOverCapacity } = useActivityPublicCapacity(activity.id)
  const resolvedEndTime = resolveActivityEndTime(activity)
  let imageUrl
  if (activity.bannerUrl) {
    imageUrl = activity.bannerUrl
  } else if (client) {
    if (client.logoFullUrl) {
      imageUrl = client.logoFullUrl
    } else if (client.logoUrl) {
      imageUrl = `/assets/logos/${client.logoUrl}`
    }
  }

  return (
    <div className="segment-container" id="registration-info">
      <div className="header">
        {imageUrl && <ActivityBanner src={imageUrl} alt={activity.name} />}
        <Typography variant="h5" component="h2" className="header-title">
          {activity.name}
        </Typography>
        {activity.time && (
          <TimeBadge direction="row" align="center" gap="6px">
            <AccessTimeIcon sx={{ fontSize: 16 }} />
            <span>
              {activity.time}
              {resolvedEndTime ? ` – ${resolvedEndTime}` : ''}
            </span>
          </TimeBadge>
        )}
      </div>

      {/* Button with Arrow Icon */}
      <Box fullWidth direction="column" gap="8px">
        {activity.dropinOnly ? (
          <DropinOnlyStatusButton />
        ) : activity.registrationStatus === ActivityRegistrationStatus.Closed ||
          activity.registrationStatus === ActivityRegistrationStatus.Full ||
          activity.registrationStatus === ActivityRegistrationStatus.Special ? (
          <RegistrationStatusButton registrationStatus={activity.registrationStatus} />
        ) : (
          <>
            {isAtOrOverCapacity && <RegistrationQueueNotice language={language} />}
            <Button
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardIcon />}
              className="action-button"
              href={buildRoute(RoutePath.REGISTER, { activityId: activity.subeventParentId || activity.id }, langQuery)}
              sx={
                isAtOrOverCapacity
                  ? {
                      backgroundColor: '#ed6c02',
                      '&:hover': { backgroundColor: '#e65100' }
                    }
                  : undefined
              }
            >
              <GrooverText
                textKey={isAtOrOverCapacity ? 'registrationCtactaRegisterQueue' : 'registrationCtactaRegister'}
                language={language}
              />
            </Button>
          </>
        )}
      </Box>

      {/* Description Text */}
      <Typography variant="subtitle1" className="description">
        <div dangerouslySetInnerHTML={{ __html: activity.description }} />
      </Typography>
    </div>
  )
}

const ActivityBanner = styled.img`
  max-width: 100%;
  max-height: 200px;
  height: auto;
  object-fit: contain;
  padding-bottom: 20px;
  display: block;
  margin: 0 auto;
  border-radius: 6px;
`

const TimeBadge = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(0, 0, 0, 0.06);
  color: #444;
  font-size: 0.95em;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  margin-top: 8px;
  margin-bottom: 16px;
  width: fit-content;
  letter-spacing: 0.3px;
`

export default RegistrationInfo
